import { computed, inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { LoggedUserService } from "./logged-user.service";
import {
  getHighestRole,
  UserRole,
  ROLE_HIERARCHY,
} from "../interfaces/user.interface";
import {
  type Permission,
  hasPermission as checkPermission,
  getModulePermission,
  getPagePermission,
} from "../config/permissions.config";

// Re-export Permission type for public API
export type { Permission } from "../config/permissions.config";

/**
 * Authorization Service
 * Provides reactive permission checks based on user roles using Angular Signals
 *
 * Usage in components:
 * ```typescript
 * protected readonly authz = inject(AuthorizationService);
 *
 * // Check role:
 * @if (authz.isProjectManagerOrHigher()) {
 *   <button>PM Action</button>
 * }
 *
 * // Check module/page permission:
 * @if (authz.canAccessModule()('planning')) {
 *   <div>Planning Module</div>
 * }
 * ```
 *
 * Usage with directives:
 * ```html
 * <button [appRequireRole]="'PROJECT_MANAGER'">PM Only</button>
 * ```
 */
@Injectable({
  providedIn: "root",
})
export class AuthorizationService {
  /** Logged user service instance */
  private readonly _loggedUserService = inject(LoggedUserService);

  /**
   * User signal converted from observable.
   * IMPORTANT: Uses userSubject$ (BehaviorSubject) instead of user$ (shareReplay).
   * This ensures the signal updates correctly on logout/login because:
   * - userSubject$ is never recreated (same reference always)
   * - clearUser() calls _userSubject.next(null) which updates all subscribers
   * - user$ is recreated on clearUser() which would break toSignal() references
   *
   * The user's `roles` are already sourced from the Cognito token: the
   * LoggedUserService merges the token's `cognito:groups` onto the user before
   * emitting it (the single merge point), so reading `roles` here agrees with
   * the backend guard, which reads the same claim.
   */
  private readonly _user = toSignal(this._loggedUserService.userSubject$);

  /** Computed signal for current user's highest role */
  readonly currentRole = computed(() => {
    const user = this._user();
    if (!user || !user.roles) {
      return null;
    }
    return getHighestRole(user.roles as UserRole[]);
  });

  /** Computed signal for current user ID */
  readonly currentUserId = computed(() => {
    const user = this._user();
    return user?.sub ?? null;
  });

  /** Computed signal for all user roles (including secondary roles like APPROVER) */
  readonly userRoles = computed(() => {
    const user = this._user();
    return (user?.roles as UserRole[]) || [];
  });

  // ========================================
  // ROLE CHECKS
  // ========================================

  /**
   * Check if user has a specific role or higher in hierarchy
   * Uses ROLE_HIERARCHY from user.interface as single source of truth
   *
   * Hierarchy (low to high):
   * APPROVER/VALIDATOR (0) < EXTERNAL_COLLABORATOR (0.5) < EMPLOYEE (1) < SALES (2) < PROJECT_MANAGER (3) < TEAM_LEAD (4) < ADMIN (5)
   */
  readonly hasRoleOrHigher = computed(() => (role: UserRole): boolean => {
    const currentRole = this.currentRole();
    if (!currentRole) return false;

    const currentLevel = ROLE_HIERARCHY[currentRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[role] || 0;

    return currentLevel >= requiredLevel;
  });

  /**
   * Check if user has exact role
   */
  readonly hasExactRole = computed(
    () =>
      (role: UserRole): boolean =>
        this.currentRole() === role,
  );

  /**
   * Check if user is EMPLOYEE
   */
  readonly isEmployee = computed(
    () => this.currentRole() === UserRole.EMPLOYEE,
  );

  /**
   * Check if user is TEAM_LEAD
   */
  readonly isTeamLead = computed(
    () => this.currentRole() === UserRole.TEAM_LEAD,
  );

  /**
   * Check if user is TEAM_LEAD or higher
   */
  readonly isTeamLeadOrHigher = computed(() =>
    this.hasRoleOrHigher()(UserRole.TEAM_LEAD),
  );

  /**
   * Check if user is PROJECT_MANAGER or higher
   */
  readonly isProjectManagerOrHigher = computed(() =>
    this.hasRoleOrHigher()(UserRole.PROJECT_MANAGER),
  );

  /**
   * Check if user is ADMIN
   */
  readonly isAdminOnly = computed(() => {
    const role = this.currentRole();
    return role === UserRole.ADMIN;
  });

  /**
   * Check if user is ADMIN
   */
  readonly isAdmin = computed(() => this.currentRole() === UserRole.ADMIN);

  /**
   * Check if user is EXTERNAL_COLLABORATOR
   */
  readonly isExternalCollaborator = computed(
    () => this.currentRole() === UserRole.EXTERNAL_COLLABORATOR,
  );

  // ========================================
  // COMPLEMENTARY ROLE CHECKS
  // These check for special roles that can be assigned alongside main roles.
  // APPROVER and VALIDATOR are complementary roles for workflow actions.
  // ========================================

  /**
   * Check if user can approve (clock) subtasks.
   * True if user has APPROVER role OR is ADMIN.
   *
   * Note: This checks userRoles() (all roles), not currentRole() (highest role),
   * because APPROVER is a complementary role that can be assigned alongside others.
   */
  readonly canApprove = computed(() => {
    const roles = this.userRoles();
    return roles.includes(UserRole.APPROVER) || roles.includes(UserRole.ADMIN);
  });

  /**
   * Check if user can validate subtasks.
   * True if user has VALIDATOR role OR is ADMIN.
   *
   * Note: This checks userRoles() (all roles), not currentRole() (highest role),
   * because VALIDATOR is a complementary role that can be assigned alongside others.
   */
  readonly canValidate = computed(() => {
    const roles = this.userRoles();
    return (
      roles.includes(UserRole.VALIDATOR) || roles.includes(UserRole.ADMIN)
    );
  });

  // ========================================
  // MODULE & PAGE PERMISSIONS
  // ========================================

  /**
   * Check if user can access a module
   * Uses centralized permissions config
   *
   * @example
   * // In template:
   * @if (authz.canAccessModule()('planning')) {
   *   <div>Planning content</div>
   * }
   */
  readonly canAccessModule = computed(() => (moduleId: string): boolean => {
    const roles = this.userRoles();
    const permission = getModulePermission(moduleId);
    return checkPermission(roles, permission);
  });

  /**
   * Check if user can access a specific page within a module
   * Uses centralized permissions config
   *
   * @example
   * // In template:
   * @if (authz.canAccessPage()('planning', 'schedule')) {
   *   <div>Schedule page content</div>
   * }
   */
  readonly canAccessPage = computed(
    () =>
      (moduleId: string, pageId: string): boolean => {
        const roles = this.userRoles();
        const permission = getPagePermission(moduleId, pageId);
        return checkPermission(roles, permission);
      },
  );

  /**
   * Get all modules the user can access
   * Returns array of module IDs
   */
  readonly accessibleModules = computed(() => {
    const roles = this.userRoles();
    const modules = ["planning", "sales", "analytics", "admin"];
    return modules.filter((moduleId) => {
      const permission = getModulePermission(moduleId);
      return checkPermission(roles, permission);
    });
  });

  /**
   * Generic permission check by permission name
   * Uses centralized permissions config
   *
   * @param permission - Permission to check (e.g., 'module:planning', 'page:planning:schedule')
   * @returns true if user has the permission
   */
  hasPermission(permission: Permission): boolean {
    const roles = this.userRoles();
    return checkPermission(roles, permission);
  }

  // ========================================
  // DATA ACCESS SCOPE
  // These helpers determine what data a user can see,
  // which is different from permission hierarchy.
  //
  // For DATA ACCESS:
  // - ADMIN: sees all data
  // - TEAM_LEAD: sees all data (admin-like scope for activities/projects/schedule)
  // - PROJECT_MANAGER: sees only their assigned projects
  // - Others: see only their assigned items
  // ========================================

  /**
   * Check if user has full data access (sees all projects/activities)
   * True for ADMIN and TEAM_LEAD roles (TEAM_LEAD has admin-like data scope)
   *
   * Note: This is different from permission hierarchy where PM > TL.
   * For data visibility, TL has broader access than PM.
   */
  readonly hasFullDataAccess = computed(() => {
    const roles = this.userRoles();
    return (
      roles.includes(UserRole.ADMIN) || roles.includes(UserRole.TEAM_LEAD)
    );
  });

  /**
   * Check if user should see only their PM-assigned projects
   * True only if user is PROJECT_MANAGER but NOT ADMIN or TEAM_LEAD
   *
   * Use this to determine if data should be filtered by projectManagerId
   */
  readonly shouldFilterByProjectManager = computed(() => {
    const roles = this.userRoles();
    const isPM = roles.includes(UserRole.PROJECT_MANAGER);
    const hasFullAccess = this.hasFullDataAccess();
    return isPM && !hasFullAccess;
  });

  /**
   * Check if the user's highest role is ADMINISTRATION (back-office).
   */
  readonly isAdministration = computed(
    () => this.currentRole() === UserRole.ADMINISTRATION,
  );

  /**
   * Check if the user can view ALL timesheets (but still edit only their own).
   * True for ADMIN and ADMINISTRATION. Editing remains gated by ownership
   * (or ADMIN) elsewhere, so ADMINISTRATION sees everyone's timesheets but can
   * only modify their own.
   */
  readonly canViewAllTimesheets = computed(
    () => this.isAdmin() || this.isAdministration(),
  );
}

// ========================================
// UTILITY FUNCTIONS FOR ROLE FILTERING
// Use these to filter lists of users by role capabilities.
// ========================================

/**
 * Roles that can approve (clock) subtasks.
 * Use this constant when filtering user lists for approver selection.
 */
export const APPROVER_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.APPROVER,
] as const;

/**
 * Roles that can validate subtasks.
 * Use this constant when filtering user lists for validator selection.
 */
export const VALIDATOR_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.VALIDATOR,
] as const;

/**
 * Check if a user's roles include any approver role.
 * Use this to filter user lists for approver selection dialogs.
 *
 * @param userRoles - Array of user roles to check
 * @returns true if user can approve
 */
export function hasApproverRole(userRoles: UserRole[]): boolean {
  return userRoles.some((role) => APPROVER_ROLES.includes(role));
}

/**
 * Check if a user's roles include any validator role.
 * Use this to filter user lists for validator selection dialogs.
 *
 * @param userRoles - Array of user roles to check
 * @returns true if user can validate
 */
export function hasValidatorRole(userRoles: UserRole[]): boolean {
  return userRoles.some((role) => VALIDATOR_ROLES.includes(role));
}
